/**
 * "Re-authorize Shopify" without a link from the failure itself: ask chatbot-api which of the
 * scopes the store is missing, and get back the managed-install link that grants them.
 *
 * Shared by the store manager's recovery card (YEPAI-12 C8) and the chat's Publishing status
 * row (a publish that failed with `scope_missing`). Both hand the result to the same
 * `ShopifyReconnectDialog`.
 */
import { getShopifyScopeUpgradeUrl } from './scopeUpgradeApi';

/**
 * 后端没给 scopes 时问的全集：Maya 的博客/页面 + Oscar 的商品。与 yepairag
 * `core/shopify/scopes.py` 的 SHOPIFY_CONTENT_SCOPES / OPS_LISTING_SCOPES / OPS_INVENTORY_SCOPES 一致。
 * 多问无害：端点只报真正缺的那几项。
 */
export const SHOPIFY_REAUTHORIZE_FALLBACK_SCOPES = [
  'write_content',
  'write_online_store_pages',
  'read_content',
  'read_online_store_pages',
  'write_products',
  'read_products',
  'write_publications',
  'read_publications',
  'write_inventory',
  'read_inventory',
  'read_locations',
];

/** 真的授权链接都走 managed install；不带这一段的（后端兜底给的 /admin/apps）点了授权不了。 */
const SHOPIFY_GRANT_PATH = '/oauth/install';

export type ShopifyReauthorizeCheck =
  | { granted: true }
  | {
      granted: false;
      missingScopes: string[];
      /** Empty when there is no link that can actually grant them — the dialog then shows none. */
      authorizeUrl: string;
    };

/**
 * Whether the store already holds `scopes` (empty → {@link SHOPIFY_REAUTHORIZE_FALLBACK_SCOPES}),
 * and if not, the link that grants them. A failed lookup reads as "not granted, no link".
 */
export async function checkShopifyReauthorize(
  tenantId: string,
  scopes: string[]
): Promise<ShopifyReauthorizeCheck> {
  let check: Awaited<ReturnType<typeof getShopifyScopeUpgradeUrl>> | null = null;
  try {
    check = await getShopifyScopeUpgradeUrl(
      tenantId,
      scopes.length > 0 ? scopes : SHOPIFY_REAUTHORIZE_FALLBACK_SCOPES
    );
  } catch {
    check = null;
  }
  if (check?.ok) return { granted: true };
  const url = check?.auth_url ?? '';
  return {
    granted: false,
    missingScopes: check?.missing?.length ? check.missing : [],
    authorizeUrl: url.includes(SHOPIFY_GRANT_PATH) ? url : '',
  };
}
